import React, {
  type ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'
import { cn } from '@/utils/mergeClassNames'

export const ACTION_TOOLTIP_DELAY_MS = 1000

let nextTooltipId = 0

type DelayedActionTooltipProps = {
  children: ReactNode
  label: string
  delay?: number
  placement?: 'top' | 'left' | 'bottom' | 'right'
  anchorClassName?: string
  tooltipClassName?: string
  wrap?: boolean
  // Suppress the tooltip, e.g. while the anchored menu is open.
  disabled?: boolean
}

export function DelayedActionTooltip({
  children,
  label,
  delay = ACTION_TOOLTIP_DELAY_MS,
  placement = 'top',
  anchorClassName = '',
  tooltipClassName,
  wrap = false,
  disabled = false,
}: DelayedActionTooltipProps) {
  const anchorRef = useRef<HTMLSpanElement>(null)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const tooltipRef = useRef<HTMLSpanElement>(null)
  const [trackingAnchor, setTrackingAnchor] = useState(false)
  const [position, setPosition] = useState<{
    left: number
    top: number
    placement: 'top' | 'left' | 'bottom' | 'right'
  } | null>(null)
  const [tooltipId] = useState(() => {
    nextTooltipId += 1
    return `delayed-action-tooltip-${nextTooltipId}`
  })

  const clearTimer = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current)
    timerRef.current = null
  }, [])

  const updatePosition = useCallback(() => {
    const rect = anchorRef.current?.getBoundingClientRect()
    if (!rect) return
    const tooltipWidth = tooltipRef.current?.getBoundingClientRect().width ?? 0
    const resolvedPlacement =
      placement === 'right' && rect.right + 8 + tooltipWidth > window.innerWidth
        ? 'left'
        : placement
    setPosition(
      resolvedPlacement === 'left'
        ? {
            left: rect.left - 8,
            top: rect.top + rect.height / 2,
            placement: resolvedPlacement,
          }
        : resolvedPlacement === 'right'
          ? {
              left: rect.right + 8,
              top: rect.top + rect.height / 2,
              placement: resolvedPlacement,
            }
          : resolvedPlacement === 'bottom'
            ? {
                left: rect.left + rect.width / 2,
                top: rect.bottom + 8,
                placement: resolvedPlacement,
              }
            : {
                left: rect.left + rect.width / 2,
                top: rect.top - 8,
                placement: resolvedPlacement,
              }
    )
  }, [placement])

  const scheduleOpen = useCallback(() => {
    clearTimer()
    if (disabled) return
    setTrackingAnchor(true)
    timerRef.current = setTimeout(updatePosition, delay)
  }, [clearTimer, delay, disabled, updatePosition])

  const close = useCallback(() => {
    clearTimer()
    setTrackingAnchor(false)
    setPosition(null)
  }, [clearTimer])

  useEffect(() => {
    if (disabled) close()
  }, [close, disabled])

  useEffect(() => clearTimer, [clearTimer])

  const isOpen = position != null
  useLayoutEffect(() => {
    // Measure the rendered tooltip before choosing its side at the screen edge.
    if (isOpen && placement === 'right') updatePosition()
  }, [isOpen, placement, updatePosition])

  useEffect(() => {
    if (!trackingAnchor) return undefined
    // MIRA-4393: Grid updates can move an anchor without firing mouseleave.
    // Close when the pointer reaches another cell, even if the source retained focus.
    const closeOutside = (event: Event) => {
      if (event.target instanceof Node && !anchorRef.current?.contains(event.target)) close()
    }
    const reposition = () => updatePosition()
    document.addEventListener('mousemove', closeOutside, true)
    document.addEventListener('mouseover', closeOutside, true)
    document.addEventListener('focusin', closeOutside, true)
    window.addEventListener('blur', close)
    window.addEventListener('resize', reposition)
    window.addEventListener('scroll', close, true)
    return () => {
      document.removeEventListener('mousemove', closeOutside, true)
      document.removeEventListener('mouseover', closeOutside, true)
      document.removeEventListener('focusin', closeOutside, true)
      window.removeEventListener('blur', close)
      window.removeEventListener('resize', reposition)
      window.removeEventListener('scroll', close, true)
    }
  }, [close, trackingAnchor, updatePosition])

  return (
    <span
      ref={anchorRef}
      className={`inline-flex ${anchorClassName}`}
      aria-describedby={position ? tooltipId : undefined}
      onMouseEnter={scheduleOpen}
      onMouseLeave={close}
      onFocus={scheduleOpen}
      onBlur={close}
    >
      {children}
      {position &&
        typeof document !== 'undefined' &&
        createPortal(
          <span
            ref={tooltipRef}
            id={tooltipId}
            role="tooltip"
            className={cn(
              'pointer-events-none fixed z-[1000000] max-w-[264px] rounded bg-[#292929] px-2 py-1 text-sm font-normal leading-5 text-white shadow-sm',
              wrap ? 'w-[240px] whitespace-normal' : 'whitespace-nowrap',
              tooltipClassName
            )}
            style={{
              left: position.left,
              top: position.top,
              transform:
                position.placement === 'left'
                  ? 'translate(-100%, -50%)'
                  : position.placement === 'right'
                    ? 'translate(0, -50%)'
                    : position.placement === 'bottom'
                      ? 'translate(-50%, 0)'
                      : 'translate(-50%, -100%)',
            }}
          >
            {label}
            <span
              aria-hidden="true"
              className={`absolute size-2 rotate-45 bg-[#292929] ${
                position.placement === 'left'
                  ? '-right-1 top-1/2 -translate-y-1/2'
                  : position.placement === 'right'
                    ? '-left-1 top-1/2 -translate-y-1/2'
                    : position.placement === 'bottom'
                      ? '-top-1 left-1/2 -translate-x-1/2'
                      : '-bottom-1 left-1/2 -translate-x-1/2'
              }`}
            />
          </span>,
          document.body
        )}
    </span>
  )
}
